import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { AppBar, Tabs, Tab, Box, CircularProgress, LinearProgress, Grid, Typography, Button } from '@mui/material';
import { t } from 'ttag';
import useStyles from './styles';
import validator from '@rjsf/validator-ajv8';
import Form from '@rjsf/mui';
import { useStoreActions } from '../../../models/RootStore';
import Terminal from "terminal-in-react";

const TabPanel = (props) => {
	const { children, value, index, ...other } = props;
	return (
		<div role='tabpanel' hidden={value !== index} id={`tabpanel-${index}`} aria-labelledby={`tab-${index}`} {...other}>
			{value === index && <Box sx={{ p: 2 }}>{children}</Box>}
		</div>
	);
};

const ConfigComponent = ({ deviceId, terminalConfig, isConnected, onRetryConnection }) => {
	const classes = useStyles();


	const { getCategories, getConfigCategory, setConfigCategory } = useStoreActions((actions) => actions.devices);
	const { addMessage } = useStoreActions((action) => action.errorMessage);
	const [selectedTab, setSelectedTab] = useState('');
	const [categories, setCategories] = useState({});
	const [formSchema, setFormSchema] = useState(null);
	const [isLoadingCategories, setIsLoadingCategories] = useState(true);
	const [isLoadingSchema, setIsLoadingSchema] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isRetrying, setIsRetrying] = useState(false);
	const [tabsOverflow, setTabsOverflow] = useState(true);
	const tabsRef = useRef(null);

	const fetchCategories = useCallback(async () => {
		if (!deviceId || !isConnected) {
			return;
		}
		setIsLoadingCategories(true);
		try {
			const response = await getCategories(deviceId);
			setCategories(response);
			const firstKey = Object.keys(response)[0] || '';
			setSelectedTab(firstKey);
		} catch (error) {
			addMessage({ message: error.message });
		} finally {
			setIsLoadingCategories(false);
		}
	}, [deviceId, isConnected, getCategories]);

	useEffect(() => {
		if (!isConnected) {
			return;
		}
		fetchCategories();
	}, [fetchCategories, isConnected]);

	const handleRetry = async () => {
		if (isRetrying) {
			return;
		}
		setIsRetrying(true);
		try {
			await onRetryConnection?.();
		} finally {
			setIsRetrying(false);
		}
	};

	const fetchSchema = useCallback(async () => {
		if (selectedTab === 'Terminal') {
			setIsLoadingSchema(false);
			return;
		}

		if (!deviceId || !selectedTab || !isConnected) {
			return;
		}
		setIsLoadingSchema(true);
		setFormSchema(null);
		try {
			const schema = await getConfigCategory({ deviceId, category: selectedTab });
			const payload = schema?.configSchema ? schema : schema?.data;
			setFormSchema(payload || null);
		} catch (error) {
			setFormSchema(null);
			addMessage({ message: error.message });
		} finally {
			setIsLoadingSchema(false);
		}
	}, [deviceId, selectedTab, isConnected, getConfigCategory, addMessage]);

	useEffect(() => {
		fetchSchema();
	}, [fetchSchema]);

	useEffect(() => {
		if (!isConnected) {
			setCategories({});
			setSelectedTab('');
			setFormSchema(null);
		}
	}, [isConnected]);

	const tabs = useMemo(() => [...Object.keys(categories), 'Terminal'], [categories]);

	useEffect(() => {
		const root = tabsRef.current;
		if (!root || isLoadingCategories) {
			return;
		}

		let measuring = false;
		const measure = () => {
			if (measuring) {
				return;
			}
			const scroller = root.querySelector('.MuiTabs-scroller');
			const flex = root.querySelector('.MuiTabs-flexContainer');
			if (!scroller || !flex) {
				return;
			}
			measuring = true;
			// Temporarily drop flex grow so we measure natural tab width, not stretched width
			root.classList.add(classes.tabsMeasuring);
			const tabsWidth = flex.scrollWidth;
			const available = scroller.clientWidth;
			root.classList.remove(classes.tabsMeasuring);
			setTabsOverflow(tabsWidth > available + 1);
			measuring = false;
		};

		measure();
		const observer = new ResizeObserver(measure);
		observer.observe(root);
		return () => observer.disconnect();
	}, [tabs, isLoadingCategories, classes.tabsMeasuring]);

	const handleChange = useCallback((event, newTab) => {
		setSelectedTab(newTab);
		if (newTab !== 'Terminal') {
			setIsLoadingSchema(true);
			setFormSchema(null);
		}
	}, []);

	const handleSubmit = async (data) => {
		if(isSubmitting){
			return;
		}
		setIsSubmitting(true);
		try {
			await setConfigCategory({ deviceId, category: selectedTab, data: data.formData });
		} catch (error) {
			addMessage({ message: error.message });
		} finally {
			setIsSubmitting(false);
		}
	};

	const renderTab = (key) => {
		if (key === 'Terminal') {
			return (
				<div className={classes.terminalContainer}>
					<Terminal
						startState="maximised"
						color="#f05347"
						outputColor="white"
						prompt={terminalConfig.promptColor}
						backgroundColor="black"
						barColor="black"
						style={terminalConfig.style}
						commands={terminalConfig.commands}
						commandPassThrough={terminalConfig.commandPassThrough}
						msg="DEVICE CONTROL"
						allowTabs={false}
						showActions={false}
					/>
				</div>
			);
		}

		if (!formSchema?.configSchema || typeof formSchema.configSchema !== 'object') {
			return (
				<Box display='flex' justifyContent='center' p={2}>
					<Typography color='textSecondary'>
						{t`Config schema is unavailable for this category.`}
					</Typography>
				</Box>
			);
		}

		return (
			<Form
				key={`form-${selectedTab}`}
				schema={formSchema.configSchema}
				uiSchema={formSchema.uiSchema || {}}
				formData={formSchema.configData}
				onSubmit={handleSubmit}
				validator={validator}
			/>
		);
	};

	if (!isConnected) {
		return (
			<div className={classes.offlineContainer}>
				{isRetrying ? (
					<Box display='flex' justifyContent='center' p={2}>
						<CircularProgress color='inherit' />
					</Box>
				) : (
					<>
						<Typography variant='h6'>{t`Device Offline`}</Typography>
						<Typography className={classes.offlineBody}>
							{t`Unable to communicate with the device. Please check that the device is powered on and connected to the network.`}
						</Typography>
						<Button variant='contained' onClick={handleRetry}>
							{t`Try again`}
						</Button>
					</>
				)}
			</div>
		);
	}

	return (
		<div className={classes.root}>
			{isLoadingCategories ? (
				<Box display='flex' justifyContent='center' p={2}>
					<CircularProgress color='inherit' />
				</Box>
			) : (
				<AppBar position='static' className={classes.tabsContainer}>
					<Tabs
						ref={tabsRef}
						value={selectedTab || ''}
						onChange={handleChange}
						aria-label='tabs'
						className={`${classes.tabs} ${!tabsOverflow ? classes.tabsFitted : ''}`}
						variant={tabsOverflow ? 'scrollable' : 'fullWidth'}
						scrollButtons={tabsOverflow ? 'auto' : false}
						allowScrollButtonsMobile={tabsOverflow}
					>
						{tabs.map((key) => (
							<Tab key={key} label={categories[key] ?? key} value={key} />
						))}
					</Tabs>
					{isSubmitting && <LinearProgress />}
				</AppBar>
			)}
			{!isLoadingCategories &&
				tabs.map((key) => (
					<TabPanel key={key} value={selectedTab} index={key} className={`${classes.tabPanel} ${key === 'Terminal' ? classes.terminalTab : ''}`}>
						{isLoadingSchema && key !== 'Terminal' ? (
							<Box display='flex' justifyContent='center' p={2}>
								<CircularProgress color='inherit' />
							</Box>
						) : renderTab(key)}
					</TabPanel>
				))}
		</div>
	);
};

export default ConfigComponent;
